import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const i18nLD = (k, d) => i18n.t(`leadDetail.${k}`, d);

import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScoreBadge, BranchPill, TagChip } from '../components/Badges';
import { TAGS, fmtDateTime, SOURCE_LABELS, fmtRelative } from '../lib/constants';
import { Sparkles, ArrowLeft, MessageSquare, Mail, Phone, Plus, Send, Trash2, RefreshCcw, AlertTriangle, CheckCircle2, Flame, Snowflake, Zap, ThermometerSun, Video, UserCog, UserX, FileSignature } from 'lucide-react';
import { toast } from 'sonner';
import { HCFProfileCard } from '../components/HCFProfileCard';
import { ManualProfileFields } from '../components/ManualProfileFields';
import PipelineStageControl from '../components/PipelineStageControl';
import LeadCalendarCard from '../components/LeadCalendarCard';
import ShowDetailsCard from '../components/ShowDetailsCard';
import { CoachPanel } from '../components/coach/CoachPanel';
import { NextStepsCard } from '../components/coach/NextStepsCard';
import PaymentPlanCard from '../components/PaymentPlanCard';
import LeadPaymentsCard from '../components/LeadPaymentsCard';
import { useAuth } from '../lib/auth-context';
import ZoomReportFormModal from '../components/ZoomReportFormModal';
import { logger } from '../lib/logger';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
// ★ Fase 59.P2 — blocchi estratti in componenti dedicati (split oversize)
import { CallAttemptsBlock } from '../components/leadDetail/CallAttemptsBlock';
import { VideoEvidenceBlock } from '../components/leadDetail/VideoEvidenceBlock';
import { TempBadge } from '../components/leadDetail/TempBadge';
import { AssignmentCard } from '../components/leadDetail/AssignmentCard';
// ★ Fase 12.C — invio contratto direttamente dalla scheda lead
import { SendContractDialog } from '../components/contracts/SendContractDialog';
// ★ Fase 13 — stato contratti del lead (badge + formattazione condivisi)
import { STATUS_META, fmtDT } from '../components/contracts/contractUtils';

// ★ Fase 32 — etichette segnali localizzate (getter → lingua attiva)
const SIGNAL_LABELS = {
  get gate_form_submitted() { return i18nLD('signals.gateForm', 'Form video gate'); },
  get cta_form_submitted() { return i18nLD('signals.ctaForm', 'Form CTA qualifica'); },
  get video_progress() { return i18nLD('signals.videoProgress', 'Video % visto'); },
  get section_explored() { return i18nLD('signals.sections', 'Sezioni esplorate'); },
  get time_on_page_5min() { return i18nLD('signals.timeOnPage', 'Tempo on-page > 5 min'); },
  get cta_secondary_click() { return i18nLD('signals.ctaSecondary', 'Click CTA secondari'); },
  get return_visit() { return i18nLD('signals.returnVisit', 'Visita ricorrente'); },
  get chat_started() { return i18nLD('signals.chatStarted', 'Chat AI iniziata'); },
  get budget_provided() { return i18nLD('signals.budget', 'Budget dichiarato'); },
  get timeline_provided() { return i18nLD('signals.timeline', 'Timeline dichiarata'); },
};

// Campi custom_fields gestiti dal nostro nuovo blocco "Stato chiamate" — non
// duplicarli nella sezione "Profilo" generica.
const CALL_FIELDS_HIDDEN = new Set([
  'call_attempts', 'last_called_at', 'last_called_by', 'last_call_outcome',
  'previous_owners', 'reassigned_at', 'abandoned_at',
  'moved_to_recuperabili_at', 'remarketing_replied', 'remarketing_replied_at',
  'remarketing_unsubscribed', 'remarketing_unsubscribed_at',
  // Organic owner — already shown as badge in header
  'is_organic', 'organic_owner_id', 'organic_owner_name', 'organic_owner_email',
  // Assigned associate — shown elsewhere
  'assigned_associate_id', 'assigned_associate_name', 'assigned_associate_email',
  'assigned_associate_telegram', 'assigned_at', 'assigned_on_shift',
  'next_dispatch_at', 'form1_submitted_at', 'dm_phase',
  'form1_video_reminders_sent',
  // Video evidence — shown in dedicated block VideoEvidenceBlock
  'video_progress_max', 'video_first_seen_at', 'video_last_seen_at',
  'video_watched_at', 'video_milestones_timeline', 'video_alert_sent_at',
]);

export default function LeadDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin', 'amministrativo'].includes((user?.role || '').toLowerCase());
  const [data, setData] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [waText, setWaText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiReply, setAiReply] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [tagToAdd, setTagToAdd] = useState('');
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  // ★ Fase 12.C — invio contratto dalla scheda lead (solo chi può inviare)
  const [contractOpen, setContractOpen] = useState(false);
  const [canSendContract, setCanSendContract] = useState(false);
  // ★ Fase 13 — contratti collegati al lead
  const [leadContracts, setLeadContracts] = useState([]);
  const [leadContractsLoading, setLeadContractsLoading] = useState(false);
  // ★ 2026-07-14: special flags per abilitare self-election come Associate
  const [specialFlags, setSpecialFlags] = useState({});

  useEffect(() => {
    api.get('/leads/_my-special-flags').then((r) => setSpecialFlags(r.data || {})).catch(() => {});
    // ★ Fase 12.C — permessi contratti (admin/amministrativo + Senior)
    api.get('/contracts/permissions')
      .then((r) => setCanSendContract(!!r.data?.can_send))
      .catch(() => setCanSendContract(false));
  }, []);

  // ★ Fase 13 — carica i contratti inviati a questo lead
  const loadLeadContracts = useCallback(() => {
    if (!canSendContract || !id) return;
    setLeadContractsLoading(true);
    api.get(`/contracts/by-lead/${id}`)
      .then((r) => setLeadContracts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLeadContracts([]))
      .finally(() => setLeadContractsLoading(false));
  }, [canSendContract, id]);

  useEffect(() => { loadLeadContracts(); }, [loadLeadContracts]);

  // Merged user object with agent-level flags (needed by AssignmentCard)
  const enrichedUser = React.useMemo(() => ({
    ...user,
    is_associate_manager: specialFlags?.is_associate_manager || false,
    is_senior: specialFlags?.is_senior || false,
  }), [user, specialFlags]);
  // ── Delete lead dialog state (admin only) ──────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Ricarica sempre "in silenzio": la scheda non viene smontata, così le
  // bozze non salvate nelle altre card (es. Profilo manuale) non si perdono.
  const load = useCallback(async () => {
    const r = await api.get(`/leads/${id}`);
    setData(r.data);
  }, [id]);

  useEffect(() => { setData(null); load(); }, [load]);

  // ★ 2026-08-14 (Fase 54) — Real-time sync ogni 15s per la scheda lead:
  // se un altro utente (es. Richard) modifica il lead, la modifica appare
  // in ≤ 15s. Refresh silenzioso (no spinner) per non disturbare la UI.
  // ★ Fase 59.P2 — dep su `load` (stabile via useCallback[id]): fix warning hooks.
  useEffect(() => {
    const REFRESH_MS = 15000;
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load({ silent: true });
    }, REFRESH_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  // ─── Memoized pure computations (perf: avoid re-compute on every render) ──
  // 2026-06-19: tag filter & custom_fields list erano calcolati inline in JSX
  // ad ogni render. Spostati in useMemo come da code review.
  // IMPORTANT: questi useMemo DEVONO stare PRIMA dell'early return per
  // rispettare le Rules of Hooks (stesso ordine di hook ad ogni render).
  const leadFromData = data?.lead;
  const availableTags = useMemo(
    () => TAGS.filter((t) => !(leadFromData?.tags || []).includes(t.key)),
    [leadFromData?.tags],
  );
  const customFieldsEntries = useMemo(
    () => Object.entries(leadFromData?.custom_fields || {})
      .filter(([k]) => !CALL_FIELDS_HIDDEN.has(k)),
    [leadFromData?.custom_fields],
  );

  if (!data) {
    return <div className="p-8 text-muted-foreground">{t('leadDetail.loading', 'Caricamento lead…')}</div>;
  }

  const { lead, activities, whatsapp_messages, emails, scheduled_emails } = data;

  const aiScore = async () => {
    setScoring(true);
    try {
      const r = await api.post('/ai/score', { lead_id: lead.id });
      toast.success(`${t('leadDetail.aiAnalyzed', 'AI ha analizzato:')} ${r.data.analysis.score}/100`);
      await load();
    } catch (e) {
      toast.error(t('leadDetail.aiScoreFail', 'AI scoring fallito'));
    } finally { setScoring(false); }
  };

  const addTag = async () => {
    if (!tagToAdd) return;
    await api.post(`/leads/${lead.id}/tags`, { tag: tagToAdd });
    setTagToAdd('');
    load();
  };
  const removeTag = async (t) => {
    await api.delete(`/leads/${lead.id}/tags/${t}`);
    load();
  };

  // ── Hard-delete del lead (solo admin) ────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.delete(`/leads/${lead.id}`);
      // ★ 2026-08-11 (Fase 37) — Soft delete: lead spostato nel cestino
      const retention = r.data?.retention_days || 30;
      toast.success(t('leadDetail.movedToTrash', 'Lead spostato nel cestino · recuperabile per {{n}} giorni', { n: retention }));
      setDeleteOpen(false);
      // Redirect alla lista lead
      nav('/app/leads', { replace: true });
    } catch (err) {
      logger.error('delete lead error:', err);
      toast.error(err?.response?.data?.detail || t('leadDetail.deleteError', 'Errore durante eliminazione'));
    } finally {
      setDeleting(false);
    }
  };

  const sendWa = async () => {
    if (!waText.trim()) return;
    if (!lead.phone) { toast.error(t('leadDetail.noPhone', 'Lead senza telefono')); return; }
    await api.post('/whatsapp/send', { lead_id: lead.id, body: waText });
    setWaText('');
    toast.success(t('leadDetail.waSent', 'Messaggio WhatsApp inviato'));
    load();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/leads/${lead.id}/notes`, { body: noteText });
    setNoteText('');
    toast.success('Nota aggiunta');
    load();
  };

  const askAi = async () => {
    if (!aiQuestion.trim()) return;
    setAiLoading(true);
    try {
      const r = await api.post('/ai/chat', { lead_id: lead.id, message: aiQuestion });
      setAiReply(r.data.reply);
    } catch (e) { toast.error(t('leadDetail.aiUnavailable', 'AI non disponibile')); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 fade-up">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="lead-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('common.back', 'Indietro')}
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="lead-detail-name">{lead.name}</h1>
            <ScoreBadge score={lead.score} />
            <BranchPill branch={lead.branch} />
            {/* ★ Fase 43 — badge funnel di provenienza (acronimo IT-AL, ES-TH, …) */}
            {(lead.funnel_code || lead.custom_fields?.funnel_code) && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--gp-gold)]/15 text-[var(--gp-forest,#0C3C2D)] border border-[var(--gp-gold)]/40 uppercase tracking-wider"
                data-testid="lead-funnel-badge"
                title={`Lead generato dal funnel ${lead.funnel_code || lead.custom_fields?.funnel_code}`}
              >
                {lead.funnel_code || lead.custom_fields?.funnel_code}
              </span>
            )}
            {(lead.custom_fields?.is_organic || lead.custom_fields?.organic_owner_id) && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
                data-testid="lead-organic-badge"
                title={`Lead organico inserito manualmente da ${lead.custom_fields?.organic_owner_name || '—'}`}
              >
                🌱 Organico di {lead.custom_fields?.organic_owner_name || '—'}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{SOURCE_LABELS[lead.source] || lead.source} · ID {lead.id?.slice(0, 8)}</div>
        </div>
        <Button onClick={aiScore} disabled={scoring} data-testid="ai-score-button">
          <Sparkles className="w-4 h-4 mr-1" /> {scoring ? 'Analisi AI…' : 'Rianalizza con AI'}
        </Button>
        {/* ★ "Ha risposto + Compila report Zoom" — opens the structured
            8-section form. Backend will email Morena + DM the original
            Associate on submit. Visible to all logged users (Associate/Senior). */}
        <Button
          onClick={() => setZoomModalOpen(true)}
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="lead-zoom-report-button"
        >
          <Video className="w-4 h-4 mr-1" /> Ha risposto · Report Zoom
        </Button>
        {/* ★ Fase 12.C — Invia contratto direttamente dalla scheda lead:
            apre il flusso di invio già precompilato con nome/email del lead.
            Visibile solo ad amministrazione e Senior (can_send). */}
        {canSendContract && (
          <Button
            onClick={() => setContractOpen(true)}
            className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
            data-testid="lead-send-contract-button"
          >
            <FileSignature className="w-4 h-4 mr-1" /> {t('leadDetail.sendContract', 'Invia contratto')}
          </Button>
        )}
        {/* ★ Delete lead — solo admin/amministrativo. AlertDialog conferma
            prima di hard-delete (lead + activities + scheduled_emails + ecc). */}
        {isAdmin && (
          <Button
            variant="outline"
            onClick={() => setDeleteOpen(true)}
            className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
            data-testid="lead-delete-button"
          >
            <Trash2 className="w-4 h-4 mr-1" /> {t('common.delete', 'Elimina')}
          </Button>
        )}
      </div>

      {/* ── Delete confirmation dialog ─────────────────────────────── */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="lead-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              {t('leadDetail.trashTitle', 'Spostare questo lead nel cestino?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('leadDetail.trashDescPre', 'Il lead')} <b>{lead.name}</b> ({lead.email || lead.phone || 'no contact'}) {t('leadDetail.trashDescPost', 'verrà spostato nel cestino e non apparirà più nelle liste, pipeline e KPI.')}
              <div className="mt-3 p-2.5 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
                ♻️ {t('leadDetail.trashRecovery', 'Potrai ripristinarlo entro 30 giorni dalla sezione Cestino del menu. Dopo questo periodo verrà cancellato definitivamente insieme a incassi, pagamenti, ordini proposte e tutti i dati collegati.')}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel">{t('common.cancel', 'Annulla')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="lead-delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? t('leadDetail.trashing', 'Sposto nel cestino…') : t('leadDetail.trashAction', 'Sposta nel cestino')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ZoomReportFormModal
        open={zoomModalOpen}
        onOpenChange={setZoomModalOpen}
        lead={lead}
        onSubmitted={() => { load(); }}
      />

      {/* ★ Fase 12.C — dialog invio contratto precompilato con i dati lead */}
      {canSendContract && (
        <SendContractDialog
          open={contractOpen}
          onClose={() => setContractOpen(false)}
          onSent={() => { load(); loadLeadContracts(); }}
          lead={lead}
        />
      )}

      {/* HCF Phase 0 — Pre-Profilazione (Scheda Cliente HCF™) */}
      <HCFProfileCard leadId={lead.id} isAdmin={isAdmin} />
      <ManualProfileFields lead={lead} onUpdated={load} />

      {/* Coach AI: analisi call, riassunto in note, prossimi step, testi */}
      <CoachPanel lead={lead} onUpdated={load} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Profile column */}
        <Card className="border-border" data-testid="lead-profile-card">
          <CardContent className="p-4 space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Contatti</div>
              <div className="mt-1 space-y-1">
                <div className="text-sm flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> {lead.email || '—'}</div>
                <div className="text-sm flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {lead.phone || '—'}</div>
              </div>
            </div>

            <div>
              <PipelineStageControl lead={lead} onUpdated={load} />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Tag</div>
              <div className="flex flex-wrap gap-1.5 min-h-[1.5rem]" data-testid="lead-tags-list">
                {(lead.tags || []).length === 0 && <span className="text-xs text-muted-foreground italic">{t('leadDetail.noTags', 'Nessun tag')}</span>}
                {(lead.tags || []).map((t) => (
                  <TagChip key={t} tagKey={t} onRemove={removeTag} />
                ))}
              </div>
              <div className="flex gap-1.5 mt-2">
                <Select value={tagToAdd} onValueChange={setTagToAdd}>
                  <SelectTrigger className="flex-1" data-testid="lead-tag-select"><SelectValue placeholder={t('leadDetail.addTag', 'Aggiungi tag…')} /></SelectTrigger>
                  <SelectContent>
                    {availableTags.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={addTag} disabled={!tagToAdd} data-testid="lead-tag-add-button"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            <div>
              <AssignmentCard lead={lead} isAdmin={isAdmin} currentUser={enrichedUser} onUpdated={load} />
            </div>

            <div>
              <NextStepsCard leadId={lead.id} />
            </div>

            {/* Pipeline v2: in-line "Show" details (Trascrizione + Proposta + Report)
                ★ 2026-07-23 (richiesta utente, opzione B): la card è sempre
                visibile una volta che ha almeno un dato salvato — non sparirà
                mai, in nessuno stage. Se non ci sono dati, la mostriamo solo
                dagli stage 'show' in poi (per permettere il primo inserimento). */}
            {(() => {
              const cf = lead.custom_fields || {};
              const hasReachedShow = Boolean(
                lead.first_reached_at?.show ||
                lead.first_reached_at?.proposta_inviata ||
                lead.first_reached_at?.chiuso ||
                (cf.first_reached_at?.show) ||
                (cf.first_reached_at?.proposta_inviata) ||
                (cf.first_reached_at?.chiuso)
              );
              const hasShowData = Boolean(
                cf.show_transcript || cf.proposta_url || cf.show_report_notes
              );
              const showStagesSet = new Set([
                'show', 'no_show', 'proposta_inviata', 'chiuso', 'perso',
              ]);
              const isShowOrBeyond = showStagesSet.has(lead.stage);
              const shouldRender = hasShowData || isShowOrBeyond || hasReachedShow;
              if (!shouldRender) return null;
              return (
                <div data-testid="show-details-wrapper">
                  <ShowDetailsCard lead={lead} onUpdated={load} />
                </div>
              );
            })()}

            {/* 2026-06-25: Payment plan (sempre visibile dopo la chiusura, modificabile) */}
            <div>
              <PaymentPlanCard leadId={lead.id} leadStage={lead.stage} onUpdated={load} />
            </div>

            {/* 2026-08-11 (Fase 37): Pagamenti registrati (proposal_payments) + delete inline */}
            <div>
              <LeadPaymentsCard leadId={lead.id} onChanged={load} />
            </div>

            {/* Pipeline v2: in-line calendar (appointments & follow-ups) */}
            <div>
              <LeadCalendarCard leadId={lead.id} />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Stato chiamate</div>
              <CallAttemptsBlock lead={lead} />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Prova visione VSL</div>
              <VideoEvidenceBlock lead={lead} />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Profilo</div>
              <div className="text-xs space-y-1">
                {customFieldsEntries.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
                    <span className="truncate text-right">{typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v ?? '—')}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground">
              {t('leadDetail.createdAt', 'Creato:')} {fmtDateTime(lead.created_at)}<br />
              {t('leadDetail.updatedAt', 'Aggiornato:')} {fmtDateTime(lead.updated_at)}
            </div>
          </CardContent>
        </Card>

        {/* Center: Tabs (Timeline / WhatsApp / Email) */}
        <Card className="lg:col-span-1 border-border">
          <CardContent className="p-0">
            <Tabs defaultValue="timeline" className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b border-border bg-[var(--surface-2)]">
                <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
                <TabsTrigger value="emails" data-testid="tab-emails">Email</TabsTrigger>
              </TabsList>
              <TabsContent value="timeline" className="p-4">
                <div className="flex gap-2 mb-3">
                  <Input placeholder={t('leadDetail.addNote', 'Aggiungi nota…')} value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="lead-note-input" />
                  <Button size="sm" onClick={addNote} data-testid="lead-note-add"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="relative pl-10 timeline-line" data-testid="lead-timeline">
                  {(activities || []).length === 0 && <div className="text-sm text-muted-foreground py-6">{t('leadDetail.noActivity', 'Nessuna attività ancora.')}</div>}
                  {(activities || []).map((a) => (
                    <div key={a.id} className="relative mb-4" data-testid="timeline-event">
                      <div className="absolute -left-7 top-1 w-3 h-3 rounded-full bg-[hsl(var(--primary))] ring-4 ring-[var(--surface-1)]" />
                      <div className="bg-card border border-border rounded-md p-3">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium">{a.title}</div>
                          <div className="text-[11px] text-muted-foreground">{fmtRelative(a.created_at)}</div>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">{a.type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="whatsapp" className="p-4">
                <div className="max-h-[420px] overflow-y-auto space-y-2 mb-3" data-testid="lead-whatsapp-thread">
                  {(whatsapp_messages || []).length === 0 && <div className="text-sm text-muted-foreground py-4">{t('leadDetail.noMessages', 'Nessun messaggio.')}</div>}
                  {(whatsapp_messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`} data-testid="whatsapp-message">
                      <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${m.direction === 'out' ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'bg-[var(--surface-2)] text-foreground'}`}>
                        <div>{m.body}</div>
                        <div className="text-[10px] opacity-70 mt-0.5">{fmtDateTime(m.created_at)}{m.status ? ` · ${m.status}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 border-t border-border pt-3">
                  <Input placeholder={lead.phone ? t('leadDetail.writeMessage', 'Scrivi un messaggio…') : t('leadDetail.phoneMissing', 'Numero di telefono mancante')} disabled={!lead.phone} value={waText} onChange={(e) => setWaText(e.target.value)} data-testid="whatsapp-composer-input" />
                  <Button size="sm" onClick={sendWa} disabled={!waText.trim() || !lead.phone} data-testid="whatsapp-send-button"><Send className="w-4 h-4" /></Button>
                </div>
                {!lead.phone && <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t('leadDetail.addPhoneToMsg', 'Aggiungi un numero di telefono per messaggiare.')}</div>}
              </TabsContent>
              <TabsContent value="emails" className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">{t('leadDetail.scheduledEmails', 'Email programmate')}</div>
                <div className="space-y-1.5 mb-4">
                  {(scheduled_emails || []).length === 0 && <div className="text-sm text-muted-foreground">{t('leadDetail.noScheduledEmails', 'Nessuna email programmata.')}</div>}
                  {(scheduled_emails || []).map((s) => (
                    <div key={s.id} className="text-xs border border-border rounded-md p-2 bg-[var(--surface-2)]" data-testid="scheduled-email-row">
                      <div className="flex justify-between">
                        <div className="font-medium truncate flex-1">{s.subject}</div>
                        <div className="text-muted-foreground ml-2">{s.status}</div>
                      </div>
                      <div className="text-muted-foreground">{s.sequence_name} · {t('leadDetail.sendAt', 'invio:')} {fmtDateTime(s.send_at)}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">{t('leadDetail.sentEmails', 'Email inviate')}</div>
                <div className="space-y-1.5">
                  {(emails || []).length === 0 && <div className="text-sm text-muted-foreground">{t('leadDetail.noSentEmails', 'Nessuna email inviata.')}</div>}
                  {(emails || []).map((e) => (
                    <div key={e.id} className="text-xs border border-border rounded-md p-2">
                      <div className="flex justify-between">
                        <div className="font-medium truncate flex-1">{e.subject}</div>
                        <div className="text-muted-foreground ml-2">{e.status}</div>
                      </div>
                      <div className="text-muted-foreground">{t('leadDetail.fromSender', 'da')} {e.from_name} · {fmtDateTime(e.sent_at || e.created_at)}</div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Lead Score panel */}
        <Card className="border-border" style={{ backgroundColor: 'hsl(var(--accent))' }} data-testid="ai-insights-panel">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <div className="text-sm font-medium">Lead Score</div>
              </div>
              <TempBadge temperature={lead.temperature} />
            </div>
            <div>
              <div className="font-serif text-5xl text-[hsl(var(--primary))] leading-none mt-1" data-testid="ai-score-value">
                {lead.score ?? '—'}
                <span className="text-base text-muted-foreground font-sans ml-1">/ 155</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {lead.score_updated_at ? `${t('leadDetail.scoreUpdated', 'Aggiornato')} ${fmtRelative(lead.score_updated_at)}` : t('leadDetail.awaitingSignals', 'In attesa di segnali')}
              </div>
            </div>

            {/* Breakdown */}
            {Array.isArray(lead.score_breakdown) && lead.score_breakdown.length > 0 && (
              <div className="pt-3 border-t border-border" data-testid="score-breakdown">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">{t('leadDetail.signalBreakdown', 'Breakdown segnali')}</div>
                <div className="space-y-1.5">
                  {lead.score_breakdown.map((b) => (
                    <div key={b.signal_type} className="flex items-center justify-between text-xs gap-2">
                      <span className="truncate text-foreground/80">{SIGNAL_LABELS[b.signal_type] || b.signal_type}</span>
                      <span className="tabular-nums font-medium text-[hsl(var(--primary))]">
                        {b.points} <span className="text-muted-foreground font-normal">/ {b.cap}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-border">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('leadDetail.nextBestAction', 'Prossima migliore azione')}</div>
              <p className="text-sm leading-relaxed mt-1" data-testid="ai-next-best-action">{lead.next_action || '—'}</p>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">{t('leadDetail.askAi', 'Chiedi all\'AI')}</div>
              <div className="flex gap-2">
                <Input placeholder={t('leadDetail.askAiPh', 'Es: come riattivare questo lead?')} value={aiQuestion} onChange={(e) => setAiQuestion(e.target.value)} data-testid="ai-chat-input" />
                <Button size="sm" onClick={askAi} disabled={aiLoading || !aiQuestion.trim()} data-testid="ai-chat-send">
                  {aiLoading ? '…' : <Send className="w-4 h-4" />}
                </Button>
              </div>
              {aiReply && (
                <div className="text-xs mt-3 bg-[var(--surface-1)] border border-border rounded-md p-2" data-testid="ai-chat-reply">{aiReply}</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ★ Fase 13 — Contratti inviati a questo lead */}
        {canSendContract && (
          <Card className="border-border" data-testid="lead-contracts-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-[hsl(var(--primary))]" />
                  <div className="text-sm font-medium">
                    Contratti
                    {leadContracts.length > 0 && (
                      <span className="ml-1.5 text-[11px] text-muted-foreground font-normal">({leadContracts.length})</span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setContractOpen(true)}
                  data-testid="lead-contracts-new-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo
                </Button>
              </div>

              {leadContractsLoading ? (
                <div className="space-y-2">
                  <div className="h-12 rounded-md bg-[var(--surface-2)] animate-pulse" />
                  <div className="h-12 rounded-md bg-[var(--surface-2)] animate-pulse" />
                </div>
              ) : leadContracts.length === 0 ? (
                <p className="text-xs text-muted-foreground italic" data-testid="lead-contracts-empty">
                  {t('leadDetail.noContracts', 'Nessun contratto inviato a questo lead.')}
                </p>
              ) : (
                <div className="space-y-2" data-testid="lead-contracts-list">
                  {leadContracts.map((c, i) => {
                    const meta = STATUS_META[c.status] || { label: c.status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                    return (
                      <div
                        key={c.id}
                        className="rounded-md border border-border bg-[var(--surface-2)]/50 p-2.5"
                        data-testid={`lead-contract-item-${i}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] text-[hsl(var(--primary))]" data-testid={`lead-contract-serial-${i}`}>
                            {c.serial_number || '—'}
                          </span>
                          <span className={`text-[10px] border rounded-full px-2 py-0.5 whitespace-nowrap ${meta.cls}`} data-testid={`lead-contract-status-${i}`}>
                            {meta.label}
                          </span>
                        </div>
                        <div className="text-xs font-medium truncate mt-1">{c.template_name}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {t('leadDetail.sentOn', 'Inviato')} {fmtDT(c.sent_at)}{c.sent_by_name ? ` · ${t('leadDetail.bySender', 'da')} ${c.sent_by_name}` : ''}
                          {c.signature?.signed_at ? ` · ${t('leadDetail.signedOn', 'firmato')} ${fmtDT(c.signature.signed_at)}` : ''}
                        </div>
                      </div>
                    );
                  })}
                  <Link
                    to="/app/contracts"
                    className="block text-[11px] text-[hsl(var(--primary))] hover:underline"
                    data-testid="lead-contracts-open-section"
                  >
                    Apri la sezione Contratti →
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
